<template>
  <Dialog v-model="visible" :closable="false">
    <div class="throw-result">
      <div class="dice-wrap">
        <div class="dice-bg"></div>
        <img class="dice-img" :class="{ rolling: animating }" :src="currentImg" alt="dice">
      </div>
      <div class="steps-text" :class="{ visible: showResult }">
        <i18n-t keypath="monopoly.throwResultUnit" tag="span" class="unit">
          <template #steps>
            <span class="num">{{ props.steps }}</span>
          </template>
        </i18n-t>
      </div>
      <Btn
        type="blue"
        class="confirm-btn"
        :disabled="!canConfirm"
        @click="handleConfirm"
      >
        {{ $t('monopoly.confirm') }}
      </Btn>
    </div>
  </Dialog>
</template>

<script setup>
import {
  computed,
  defineProps,
  defineEmits,
  ref,
  watch,
  onBeforeUnmount,
} from 'vue'
import Dialog from '../Dialog/index.vue'
import Btn from '../Btn/index.vue'
import saizi1 from '@/views/act/monopoly/assets/img/ani-dices/saizi1.png'
import saizi2 from '@/views/act/monopoly/assets/img/ani-dices/saizi2.png'
import saizi3 from '@/views/act/monopoly/assets/img/ani-dices/saizi3.png'
import saizi4 from '@/views/act/monopoly/assets/img/ani-dices/saizi4.png'
import saizi5 from '@/views/act/monopoly/assets/img/ani-dices/saizi5.png'
import saizi6 from '@/views/act/monopoly/assets/img/ani-dices/saizi6.png'
import saizi1Qx from '@/views/act/monopoly/assets/img/ani-dices/saizi1_QX.png'
import saizi2Qx from '@/views/act/monopoly/assets/img/ani-dices/saizi2_QX.png'
import saizi3Qx from '@/views/act/monopoly/assets/img/ani-dices/saizi3_QX.png'
import saizi4Qx from '@/views/act/monopoly/assets/img/ani-dices/saizi4_QX.png'
import saizi5Qx from '@/views/act/monopoly/assets/img/ani-dices/saizi5_QX.png'
import saizi6Qx from '@/views/act/monopoly/assets/img/ani-dices/saizi6_QX.png'

const BLUR_FRAMES = [saizi1, saizi2, saizi3, saizi4, saizi5, saizi6]
const CLEAR_FRAMES = [saizi1Qx, saizi2Qx, saizi3Qx, saizi4Qx, saizi5Qx, saizi6Qx]

// 方案1：拖影快切 + 缓停（缓停段多切几帧模糊图），最后定格清晰结果
const FAST_ROUNDS = 10
const SLOW_ROUNDS = 8
const FRAME_MS_FAST = 75
const FRAME_MS_SLOW_START = 110
const FRAME_MS_SLOW_END = 200

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  steps: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const animating = ref(false)
const showResult = ref(false)
const currentImg = ref(CLEAR_FRAMES[0])
let timers = []
let playToken = 0
let finished = false

const canConfirm = computed(() => showResult.value && !animating.value)

const clearTimers = () => {
  timers.forEach((id) => clearTimeout(id))
  timers = []
}

const sleep = (ms) =>
  new Promise((resolve) => {
    const id = setTimeout(resolve, ms)
    timers.push(id)
  })

const normalizeSteps = (steps) => {
  const n = Number(steps) || 1
  return Math.min(6, Math.max(1, n))
}

const easeOutCubic = (t) => 1 - (1 - t) ** 3

const handleConfirm = () => {
  if (!canConfirm.value || finished) return
  finished = true
  clearTimers()
  visible.value = false
  emit('confirm')
}

const playAnimation = async (token) => {
  const resultIndex = normalizeSteps(props.steps) - 1
  animating.value = true
  showResult.value = false
  currentImg.value = BLUR_FRAMES[0]

  let frameCursor = 0

  // 快速段：均匀切模糊图
  for (let i = 0; i < FAST_ROUNDS; i++) {
    if (token !== playToken) return
    frameCursor = (resultIndex + i) % BLUR_FRAMES.length
    currentImg.value = BLUR_FRAMES[frameCursor]
    await sleep(FRAME_MS_FAST)
  }

  // 缓停段：多切几帧模糊图，间隔逐渐拉长，末尾贴向结果
  for (let i = 0; i < SLOW_ROUNDS; i++) {
    if (token !== playToken) return

    const t = i / Math.max(1, SLOW_ROUNDS - 1)
    const eased = easeOutCubic(t)

    let frameIndex
    if (i >= SLOW_ROUNDS - 2) {
      frameIndex = resultIndex
    } else if (i >= SLOW_ROUNDS - 4 && i % 2 === 0) {
      frameIndex = resultIndex
    } else {
      frameCursor = (frameCursor + 1) % BLUR_FRAMES.length
      frameIndex = frameCursor
    }

    currentImg.value = BLUR_FRAMES[frameIndex]
    const frameMs =
      FRAME_MS_SLOW_START + (FRAME_MS_SLOW_END - FRAME_MS_SLOW_START) * eased
    await sleep(frameMs)
  }

  if (token !== playToken) return
  currentImg.value = CLEAR_FRAMES[resultIndex]
  animating.value = false
  showResult.value = true
}

watch(
  () => props.modelValue,
  (val) => {
    clearTimers()
    playToken += 1
    if (val) {
      finished = false
      playAnimation(playToken)
    } else {
      animating.value = false
    }
  }
)

onBeforeUnmount(() => {
  playToken += 1
  clearTimers()
})
</script>

<style lang="less" scoped>
.throw-result {
  width: 100%;
  text-align: center;
  color: #12215c;
  padding: 20px 0 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.dice-wrap {
  position: relative;
  margin: 0 auto;
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dice-bg {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 240px;
  height: 240px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background:
    radial-gradient(circle at center,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(255, 255, 255, 0.55) 38%,
      rgba(168, 232, 255, 0.45) 62%,
      rgba(145, 214, 245, 0.35) 78%,
      transparent 100%);
  box-shadow:
    inset 0 0 18px rgba(255, 255, 255, 0.9),
    0 0 24px rgba(255, 255, 255, 0.55);
  pointer-events: none;
}

.dice-img {
  position: relative;
  z-index: 1;
  width: 180px;
  height: auto;
  transition: transform 0.05s linear;
  filter: drop-shadow(0 10px 12px rgba(30, 80, 120, 0.22));

  &.rolling {
    transform: scale(1.05) rotate(-8deg);
  }
}

.steps-text {
  // 始终占位，避免结果文案出现时弹窗高度突变抖动
  height: 58px;
  margin-top: 12px;
  font-size: 48px;
  font-weight: bold;
  line-height: 58px;
  visibility: hidden;
  opacity: 0;

  &.visible {
    visibility: visible;
    opacity: 1;
  }

  .num {
    color: #e89a2e;
    margin-right: 6px;
  }

  .unit {
    color: #1a2b5c;
  }
}

.confirm-btn {
  margin: 28px auto 0;
}
</style>
